/**
 * TanStack Query hooks wrapping the typed API client.
 * Replaces mock data in Lovable views with real backend calls.
 */
import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { z } from "zod";

// ─── Schemas ─────────────────────────────────────────────────────────
export const PatientSchema = z.object({
  id: z.string(),
  mrn: z.string(),
  ipp: z.string().nullable().optional(),
  nationalId: z.string().nullable().optional(),
  family: z.string(),
  given: z.string(),
  familyAr: z.string().nullable().optional(),
  givenAr: z.string().nullable().optional(),
  birthDate: z.string(),
  gender: z.string(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  bloodGroup: z.string().nullable().optional(),
  photoUrl: z.string().nullable().optional(),
});
export type Patient = z.infer<typeof PatientSchema>;

export const AppointmentSchema = z.object({
  id: z.string(),
  startAt: z.string(),
  endAt: z.string(),
  status: z.string(),
  type: z.string(),
  reason: z.string().nullable().optional(),
  patient: z.object({
    id: z.string(),
    family: z.string(),
    given: z.string(),
    mrn: z.string(),
    phone: z.string().nullable().optional(),
  }),
  doctor: z.object({
    id: z.string(),
    fullName: z.string(),
    primarySpecialty: z.string().nullable().optional(),
  }),
});
export type Appointment = z.infer<typeof AppointmentSchema>;

export const DoctorSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  email: z.string(),
  primarySpecialty: z.string().nullable().optional(),
  facilityId: z.string().nullable().optional(),
  departmentId: z.string().nullable().optional(),
  inpe: z.string().nullable().optional(),
  cnomRegistration: z.string().nullable().optional(),
});
export type Doctor = z.infer<typeof DoctorSchema>;

export const InvoiceSchema = z.object({
  id: z.string(),
  number: z.string(),
  type: z.string(),
  status: z.string(),
  paymentMethod: z.string().nullable().optional(),
  insuranceType: z.string().nullable().optional(),
  insuranceCoveragePercent: z.number().nullable().optional(),
  totalMillimes: z.union([z.string(), z.number()]).transform(String),
  paidMillimes: z.union([z.string(), z.number()]).transform(String),
  issuedAt: z.string().nullable().optional(),
  paidAt: z.string().nullable().optional(),
  patient: z.object({
    id: z.string(),
    family: z.string(),
    given: z.string(),
    mrn: z.string(),
  }).optional(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

// ─── Query keys ─────────────────────────────────────────────────────
export const qk = {
  patients: (q?: string) => ["patients", q] as const,
  patient: (id: string) => ["patients", id] as const,
  timeline: (id: string) => ["patients", id, "timeline"] as const,
  agenda: (from: string, to: string) => ["agenda", from, to] as const,
  doctors: () => ["config", "doctors"] as const,
  facilities: () => ["config", "facilities"] as const,
  specialties: () => ["config", "specialties"] as const,
  tenant: () => ["config", "tenant"] as const,
  invoices: () => ["billing", "invoices"] as const,
};

// ─── Patients ───────────────────────────────────────────────────────
export function usePatients(q?: string) {
  return useQuery({
    queryKey: qk.patients(q),
    queryFn: () => apiFetch(`/api/patients?q=${encodeURIComponent(q ?? "")}&limit=50`, {
      schema: z.object({ items: z.array(PatientSchema), total: z.number() }),
    }),
  });
}

export function usePatient(id: string | undefined) {
  return useQuery({
    queryKey: qk.patient(id ?? ""),
    queryFn: () => apiFetch(`/api/patients/${id}`, { schema: PatientSchema.extend({
      insurances: z.array(z.object({ id: z.string(), type: z.string(), number: z.string(), coveragePercent: z.number() })).optional(),
      allergies: z.array(z.object({ id: z.string(), substance: z.string(), type: z.string(), severity: z.string(), criticality: z.string() })).optional(),
    }) }),
    enabled: !!id,
  });
}

export function usePatientTimeline(id: string | undefined) {
  return useQuery({
    queryKey: qk.timeline(id ?? ""),
    queryFn: () => apiFetch(`/api/patients/${id}/timeline`, {
      schema: z.object({ items: z.array(z.object({
        id: z.string(), occurredAt: z.string(), eventType: z.string(),
        payload: z.record(z.unknown()), resourceType: z.string().nullable().optional(),
      })) }),
    }),
    enabled: !!id,
  });
}

// ─── Agenda ─────────────────────────────────────────────────────────
export function useAgenda(from: string, to: string) {
  return useQuery({
    queryKey: qk.agenda(from, to),
    queryFn: () => apiFetch(`/api/agenda?from=${from}&to=${to}`, {
      schema: z.object({ items: z.array(AppointmentSchema), from: z.string(), to: z.string() }),
    }),
  });
}

export function useCreateAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      patientId: string; doctorId: string; facilityId: string;
      startAt: string; endAt: string; reason?: string; type?: string;
    }) => apiFetch(`/api/agenda`, {
      method: "POST",
      body: data,
      schema: AppointmentSchema,
      auditAction: "appointment.create",
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agenda"] }),
  });
}

export function useUpdateAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; status?: string; notes?: string }) =>
      apiFetch(`/api/agenda/${id}`, {
        method: "PATCH",
        body: patch,
        schema: AppointmentSchema,
        auditAction: `appointment.${patch.status ?? "update"}`,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agenda"] }),
  });
}

// ─── Config ─────────────────────────────────────────────────────────
export function useDoctors() {
  return useQuery({
    queryKey: qk.doctors(),
    queryFn: () => apiFetch(`/api/config/doctors`, { schema: z.object({ items: z.array(DoctorSchema) }) }),
  });
}

export function useSpecialties() {
  return useQuery({
    queryKey: qk.specialties(),
    queryFn: () => apiFetch(`/api/config/specialties`, { schema: z.record(z.array(z.string())) }),
    staleTime: 1000 * 60 * 60, // 1h
  });
}

// ─── Invoices / Billing ─────────────────────────────────────────────
export function useInvoices() {
  return useQuery({
    queryKey: qk.invoices(),
    queryFn: () => apiFetch(`/api/billing/invoices`, { schema: z.object({ items: z.array(InvoiceSchema) }) }),
  });
}

// ─── AI Advisor ─────────────────────────────────────────────────────
export function useSuggestIcd10() {
  return useMutation({
    mutationFn: (data: { patientId: string; chiefComplaint: string; hpi?: string; examination?: string }) =>
      apiFetch(`/api/ai/suggest-icd10`, {
        method: "POST",
        body: data,
        schema: z.object({
          isAdvisoryOnly: z.literal(true),
          requiresHumanReview: z.literal(true),
          confidence: z.number(),
          reasoning: z.string(),
          suggestions: z.array(z.object({
            code: z.string(), label: z.string(), rationale: z.string(), evidence: z.array(z.string()).optional(),
          })),
        }),
      }),
  });
}
